import { useEffect, useState } from "react";
import { BankErrors } from "@/domains/bank/constants";
import toast from "@/domains/toasts/helpers/toast";
import { useBankCharacterQuery } from "@/domains/bank/hooks/useBankCharacterQuery";
import { useBankTariffsQuery } from "@/domains/bank/hooks/useBankTariffs";
import { useCreateBankAccountMutation } from "@/domains/bank/hooks/useCreateBankAccountMutation";
import { useShowBankBranch } from "@/domains/bank/hooks/useShowBankBranch";
import { BankBranchModal } from "@/domains/bank/components/BankBranchModal";
import { OnboardingStep01 } from "@/domains/bank/components/Onboarding/OnboardingStep01";
import { OnboardingStep02 } from "@/domains/bank/components/Onboarding/OnboardingStep02";
import { OnboardingStep03 } from "@/domains/bank/components/Onboarding/OnboardingStep03";

export function BankBranchContainer() {
  const [onboardingStep, setOnboardingStep] = useState<"welcome" | "selectTariff" | "openAccount">(
    "welcome",
  );
  const { isOpen, close, filialId } = useShowBankBranch();
  const [tariffId, setTariffId] = useState<number | null>(null);
  const {
    data: character,
    isLoading: isCharacterLoading,
    refetch: refetchCharacter,
  } = useBankCharacterQuery({ enabled: isOpen });
  const { data: tariffs, isLoading: isTariffsLoading } = useBankTariffsQuery({
    enabled: isOpen,
  });
  const createAccountMutation = useCreateBankAccountMutation();

  useEffect(() => {
    if (!isOpen) {
      setOnboardingStep("welcome");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  if (character && !character.hasBankAccount) {
    if (onboardingStep === "welcome") {
      return (
        <OnboardingStep01
          minServicePrice={tariffs?.minServicePrice}
          onSubmit={() => setOnboardingStep("selectTariff")}
        />
      );
    }

    if (onboardingStep === "selectTariff") {
      return (
        <OnboardingStep02
          onSubmit={() => setOnboardingStep("openAccount")}
          onCancel={() => setOnboardingStep("welcome")}
          tariffs={tariffs?.tariffs ?? []}
          selectedTariffId={tariffId ?? undefined}
          onTariffSelect={(id) => setTariffId(id)}
        />
      );
    }

    if (onboardingStep === "openAccount") {
      return (
        <OnboardingStep03
          onSubmit={() => {
            createAccountMutation.mutate(tariffId, {
              onSuccess: () => {
                toast.success("Вы успешно открыли банковский счёт.");
              },
              onError: (error) => {
                if (error?.message === BankErrors.NOT_ENOUGH_MONEY_ERROR) {
                  toast.error("Недостаточно наличных для открытия счёта!");
                  return;
                }

                toast.error(error.message);
              },
            });
          }}
          onCancel={() => setOnboardingStep("selectTariff")}
          tariffName={
            tariffs?.tariffs.find((tariff) => tariff.id === tariffId)?.name ?? "Неизвестный тариф"
          }
          tariffPrice={tariffs?.tariffs.find((tariff) => tariff.id === tariffId)?.price ?? 0}
          isLoading={createAccountMutation.isPending}
        />
      );
    }
  }

  return <BankBranchModal open={isOpen} onClose={close} filialId={filialId} />;
}
